import React from 'react'
import styled from 'styled-components'
import { DEFAULT_THRESHOLD_COLOR } from './api'

export type ThresholdBreach = { direction: 'min' | 'max'; color: string } | null

// the width/table/uom of a value is not attribute-checked anywhere upstream, so a channel's current
// value can genuinely be a string, a number, or - since events ultimately carry arbitrary JSON -
// a boolean, an object, an array, null or undefined. Only the first two are ever meaningful for a
// threshold comparison, so this never throws and never coerces something like [] or {} into a number.
export const toComparableNumber = (value: unknown): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value)

        return Number.isFinite(parsed) ? parsed : null
    }

    return null
}

// max is checked before min so a channel misconfigured with min > max still reports at most one breach
export const getThresholdBreach = (
    value: unknown,
    minThreshold: number | undefined,
    maxThreshold: number | undefined,
    minThresholdColor: string | undefined,
    maxThresholdColor: string | undefined
): ThresholdBreach => {
    const numericValue = toComparableNumber(value)

    if (numericValue == null) return null

    if (typeof maxThreshold === 'number' && Number.isFinite(maxThreshold) && numericValue > maxThreshold) {
        return { direction: 'max', color: maxThresholdColor ?? DEFAULT_THRESHOLD_COLOR }
    }

    if (typeof minThreshold === 'number' && Number.isFinite(minThreshold) && numericValue < minThreshold) {
        return { direction: 'min', color: minThresholdColor ?? DEFAULT_THRESHOLD_COLOR }
    }

    return null
}

// falls back to returning the input unchanged (rather than a broken rgba(NaN,...) string) if it isn't
// a plain 6-digit hex - defensive since threshold colors can come from a channel metadata map as well
// as the color picker, and the former isn't validated anywhere upstream
export const hexToRgba = (hex: string, alpha: number): string => {
    const match = /^#?([0-9a-fA-F]{6})$/.exec(hex ?? '')

    if (!match) return hex ?? DEFAULT_THRESHOLD_COLOR

    const intValue = parseInt(match[1], 16)
    const r = (intValue >> 16) & 255
    const g = (intValue >> 8) & 255
    const b = intValue & 255

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// how much a breached row/card's background is tinted by the threshold color - a highlight, not a flat fill
export const THRESHOLD_TINT_ALPHA = 0.14

type Props = {
    breach: ThresholdBreach
}

export const ThresholdArrow = ({ breach }: Props): JSX.Element => {
    if (!breach) return null

    return (
        <ThresholdArrowIcon $color={breach.color}>
            <i className={`fa fa-arrow-${breach.direction === 'max' ? 'up' : 'down'}`} />
        </ThresholdArrowIcon>
    )
}

const ThresholdArrowIcon = styled.span<{ $color: string }>`
    flex-shrink: 0;
    margin-left: 4px;
    font-size: 11px;
    font-weight: 900;
    color: ${({ $color }) => $color};
`
